import React, { useState, useEffect } from 'react';
import {
  FiRefreshCw, FiCheck, FiX, FiSave, FiEdit2, FiAlertCircle,
  FiCheckCircle, FiInfo, FiDollarSign, FiSliders, FiZap,
} from 'react-icons/fi';
import { getAdminPlans, updateAdminPlan, AdminPlanConfig } from '../../api/admin';

/* ── styles par plan ──────────────────────────────────────────────────────────── */
const PLAN_STYLE: Record<string, { border: string; header: string; text: string; tab: string; tabActive: string }> = {
  free:       { border: 'border-gray-300',   header: 'bg-gray-50',     text: 'text-gray-700',   tab: 'border-gray-300',   tabActive: 'border-gray-600 text-gray-800 bg-white' },
  standard:   { border: 'border-blue-300',   header: 'bg-blue-50',     text: 'text-blue-700',   tab: 'border-blue-200',   tabActive: 'border-blue-600 text-blue-700 bg-white' },
  entreprise: { border: 'border-purple-300', header: 'bg-purple-50',   text: 'text-purple-700', tab: 'border-purple-200', tabActive: 'border-purple-600 text-purple-700 bg-white' },
  business:   { border: 'border-orange-300', header: 'bg-orange-50',   text: 'text-orange-700', tab: 'border-orange-200', tabActive: 'border-orange-600 text-orange-700 bg-white' },
};

/* ── features connues et leurs options ───────────────────────────────────────── */
const FEATURE_LABELS: Record<string, string> = {
  multicloud:        'Multi-cloud (Azure/AWS/GCP)',
  export_terraform:  'Export Terraform',
  export_bicep:      'Export Bicep',
  cost_estimation:   'Estimation des coûts',
  monitoring:        'Monitoring',
  finops_advanced:   'FinOps avancé',
  sso:               'SSO (SAML / OAuth)',
  audit_logs:        'Logs d\'audit',
  api_access:        'Accès API',
  white_label:       'White Label',
  dedicated_support: 'Support dédié',
  priority_support:  'Support prioritaire 24/7',
};

const FEATURE_OPTIONS: Record<string, Array<{ value: string | boolean; label: string }>> = {
  cost_estimation: [
    { value: false,      label: '✗ Désactivé' },
    { value: 'basic',    label: 'Basique' },
    { value: 'standard', label: 'Complet' },
    { value: 'advanced', label: 'Avancé' },
  ],
  monitoring: [
    { value: false,      label: '✗ Désactivé' },
    { value: 'basic',    label: 'Basique' },
    { value: 'standard', label: 'Complet (temps réel)' },
    { value: 'advanced', label: 'Avancé + alertes' },
  ],
  finops_advanced: [
    { value: false,      label: '✗ Désactivé' },
    { value: 'standard', label: 'Standard (dashboards)' },
    { value: 'advanced', label: 'Avancé (IA)' },
  ],
  audit_logs: [
    { value: false,        label: '✗ Désactivé' },
    { value: 'retention_30', label: 'Rétention 30 jours' },
    { value: 'unlimited',    label: 'Illimité' },
  ],
  api_access: [
    { value: false,       label: '✗ Désactivé' },
    { value: 'limited',   label: 'Limité (10k/mois)' },
    { value: 'unlimited', label: 'Illimité' },
  ],
};

/* ── helpers ──────────────────────────────────────────────────────────────────── */
const numInput = (val: number) => (val === -1 ? '' : String(val));
const parseQuota = (s: string): number => (s === '' || s === '∞') ? -1 : parseInt(s) || 0;

const Section: React.FC<{ title: string; icon: React.ReactNode; children: React.ReactNode }> = ({ title, icon, children }) => (
  <div className="mb-6">
    <h4 className="flex items-center gap-1.5 text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
      {icon} {title}
    </h4>
    {children}
  </div>
);

const Field: React.FC<{ label: string; children: React.ReactNode; hint?: string }> = ({ label, children, hint }) => (
  <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
    <div>
      <span className="text-sm text-gray-700">{label}</span>
      {hint && <p className="text-xs text-gray-400">{hint}</p>}
    </div>
    <div className="ml-4">{children}</div>
  </div>
);

/* ── composant principal ─────────────────────────────────────────────────────── */
const AdminPlans: React.FC = () => {
  const [plans, setPlans]       = useState<Record<string, AdminPlanConfig>>({});
  const [source, setSource]     = useState('');
  const [selected, setSelected] = useState('free');
  const [draft, setDraft]       = useState<AdminPlanConfig | null>(null);
  const [dirty, setDirty]       = useState(false);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [success, setSuccess]   = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAdminPlans();
      setPlans(data.plans);
      setSource(data.source);
      const firstKey = Object.keys(data.plans)[0] || 'free';
      setSelected(s => (data.plans[s] ? s : firstKey));
    } catch {
      setError('Impossible de charger les plans');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  /* Quand l'onglet change → clone le plan sélectionné dans draft */
  useEffect(() => {
    if (plans[selected]) {
      setDraft(JSON.parse(JSON.stringify(plans[selected])));
      setDirty(false);
      setSuccess(false);
    }
  }, [selected, plans]);

  const set = (field: keyof AdminPlanConfig, val: unknown) => {
    setDraft(d => d ? { ...d, [field]: val } : d);
    setDirty(true);
  };

  const setFeature = (key: string, val: boolean | string) => {
    setDraft(d => d ? { ...d, features: { ...d.features, [key]: val } } : d);
    setDirty(true);
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await updateAdminPlan(selected, draft);
      setPlans(p => ({ ...p, [selected]: draft }));
      setDirty(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch {
      setError('Échec de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const planKeys = Object.keys(plans);
  const s = PLAN_STYLE[selected] || PLAN_STYLE.free;

  return (
    <div className="p-6 max-w-4xl mx-auto">

      {/* ── Header ── */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Plans d'abonnement</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            {source === 'database'
              ? 'Données depuis la base de données — modifications sauvegardées immédiatement'
              : 'Lecture depuis plans.js (DB vide)'}
          </p>
        </div>
        <button onClick={load} className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition">
          <FiRefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Actualiser
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
          <FiAlertCircle size={15} /> {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
          <FiCheckCircle size={15} /> Plan <strong>{selected}</strong> sauvegardé avec succès.
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-400 gap-2">
          <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          Chargement…
        </div>
      ) : !draft ? null : (
        <>
          {/* ── Onglets ── */}
          <div className="flex gap-2 mb-6 flex-wrap">
            {planKeys.map(key => {
              const ps = PLAN_STYLE[key] || PLAN_STYLE.free;
              return (
                <button
                  key={key}
                  onClick={() => {
                    if (dirty && !window.confirm('Des modifications non sauvegardées seront perdues. Continuer ?')) return;
                    setSelected(key);
                  }}
                  className={`px-5 py-2 rounded-lg text-sm font-semibold border-2 transition ${
                    selected === key ? ps.tabActive : 'border-gray-200 text-gray-500 hover:border-gray-300 bg-gray-50'
                  }`}
                >
                  {(plans[key].display_name || key).toUpperCase()}
                  {selected === key && dirty && <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-orange-400 inline-block" />}
                </button>
              );
            })}
          </div>

          {/* ── Formulaire ── */}
          <div className={`bg-white rounded-xl border-2 ${s.border} overflow-hidden`}>

            {/* En-tête plan */}
            <div className={`${s.header} px-6 py-4 border-b border-gray-100 flex items-center justify-between`}>
              <div>
                <h3 className={`text-lg font-bold ${s.text}`}>{(draft.display_name || selected).toUpperCase()}</h3>
                {draft.updated_at && (
                  <p className="text-xs text-gray-400 mt-0.5">
                    Dernière modification : {new Date(draft.updated_at).toLocaleString('fr-FR')}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                  <span>Plan actif</span>
                  <input type="checkbox" checked={draft.is_active} onChange={e => set('is_active', e.target.checked)}
                    className="w-4 h-4 accent-blue-600" />
                </label>
                <button
                  onClick={save}
                  disabled={!dirty || saving}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition ${
                    dirty
                      ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {saving
                    ? <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Sauvegarde…</>
                    : <><FiSave size={14} /> Sauvegarder</>
                  }
                </button>
              </div>
            </div>

            <div className="p-6 space-y-2">

              {/* ── TARIFICATION ── */}
              <Section title="Tarification" icon={<FiDollarSign size={13} />}>
                <Field label="Prix mensuel (centimes)" hint="Ex: 3900 = 39,00 €">
                  <div className="flex items-center gap-2">
                    <input
                      type="number" min={0}
                      value={draft.price_monthly_cents ?? ''}
                      onChange={e => set('price_monthly_cents', e.target.value === '' ? null : parseInt(e.target.value))}
                      placeholder="0 = Gratuit, vide = Sur mesure"
                      className="w-44 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                    {draft.price_monthly_cents !== null && draft.price_monthly_cents !== undefined && (
                      <span className="text-sm text-gray-500">= {(draft.price_monthly_cents / 100).toFixed(2)} €/mois</span>
                    )}
                  </div>
                </Field>
                <Field label="Prix annuel (centimes)" hint="Ex: 37440 = 374,40 €/an (≈ -20%)">
                  <div className="flex items-center gap-2">
                    <input
                      type="number" min={0}
                      value={draft.price_yearly_cents ?? ''}
                      onChange={e => set('price_yearly_cents', e.target.value === '' ? null : parseInt(e.target.value))}
                      placeholder="vide = Sur mesure"
                      className="w-44 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                    {draft.price_yearly_cents ? (
                      <span className="text-sm text-gray-500">= {(draft.price_yearly_cents / 100).toFixed(2)} €/an ({((draft.price_yearly_cents / 100) / 12).toFixed(2)} €/mois)</span>
                    ) : null}
                  </div>
                </Field>
                <Field label="Afficher le prix publiquement">
                  <input type="checkbox" checked={draft.display_price} onChange={e => set('display_price', e.target.checked)}
                    className="w-4 h-4 accent-blue-600" />
                </Field>
                <Field label="Jours d'essai gratuit">
                  <input type="number" min={0} max={90} value={draft.trial_days}
                    onChange={e => set('trial_days', parseInt(e.target.value) || 0)}
                    className="w-24 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
                </Field>
              </Section>

              {/* ── QUOTAS ── */}
              <Section title="Quotas" icon={<FiZap size={13} />}>
                {(
                  [
                    ['projects',                   'Projets'],
                    ['architectures',               'Architectures'],
                    ['deployments_per_month',       'Déploiements / mois'],
                    ['pipeline_minutes_per_month',  'Minutes pipeline / mois'],
                    ['users_per_organization',      'Membres inclus / organisation'],
                    ['storage_gb',                  'Stockage (Go)'],
                    ['concurrent_builds',           'Builds simultanés'],
                  ] as [keyof AdminPlanConfig, string][]
                ).map(([field, label]) => (
                  <Field key={field} label={label} hint="-1 ou vide = illimité">
                    <div className="flex items-center gap-2">
                      <input
                        type="number" min={-1}
                        value={numInput(draft[field] as number)}
                        onChange={e => set(field, parseQuota(e.target.value))}
                        placeholder="∞ illimité"
                        className="w-28 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                      {(draft[field] as number) === -1 && (
                        <span className="text-xs text-green-600 font-semibold">∞ Illimité</span>
                      )}
                    </div>
                  </Field>
                ))}

                {/* Sièges additionnels */}
                <Field
                  label="Coût siège additionnel (centimes)"
                  hint="Laisser vide = pas d'addon possible (ex: FREE). 1000 = 10€"
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="number" min={0}
                      value={draft.member_price_per_additional_cents ?? ''}
                      onChange={e => set('member_price_per_additional_cents', e.target.value === '' ? null : parseInt(e.target.value))}
                      placeholder="vide = désactivé"
                      className="w-36 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                    {draft.member_price_per_additional_cents != null && draft.member_price_per_additional_cents > 0 && (
                      <span className="text-xs text-gray-500">= {(draft.member_price_per_additional_cents / 100).toFixed(0)}€/siège/mois</span>
                    )}
                  </div>
                </Field>
                <Field
                  label="Max membres total (inclus + addons)"
                  hint="Laisser vide = illimité. Ex : 5 pour STANDARD, 20 pour ENTERPRISE"
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="number" min={1}
                      value={draft.member_max_total ?? ''}
                      onChange={e => set('member_max_total', e.target.value === '' ? null : parseInt(e.target.value))}
                      placeholder="∞ illimité"
                      className="w-28 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                    {draft.member_max_total == null && (
                      <span className="text-xs text-green-600 font-semibold">∞ Illimité</span>
                    )}
                  </div>
                </Field>
              </Section>

              {/* ── AZURE DEVOPS ── */}
              <Section title="Azure DevOps" icon={<FiSliders size={13} />}>
                <Field label="Organisation ADO">
                  <input type="text" value={draft.azure_devops_org || ''}
                    onChange={e => set('azure_devops_org', e.target.value)}
                    className="w-56 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
                </Field>
                <Field label="Organisation partagée" hint="Si décoché : chaque client a sa propre org.">
                  <input type="checkbox" checked={draft.shared_organization}
                    onChange={e => set('shared_organization', e.target.checked)}
                    className="w-4 h-4 accent-blue-600" />
                </Field>
              </Section>

              {/* ── FEATURES ── */}
              <Section title="Fonctionnalités" icon={<FiEdit2 size={13} />}>
                {Object.keys(FEATURE_LABELS).map(fk => {
                  const val = draft.features[fk];
                  const options = FEATURE_OPTIONS[fk];

                  return (
                    <Field key={fk} label={FEATURE_LABELS[fk]}>
                      {options ? (
                        <select
                          value={String(val ?? false)}
                          onChange={e => {
                            const v = e.target.value;
                            setFeature(fk, v === 'true' ? true : v === 'false' ? false : v);
                          }}
                          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-white"
                        >
                          {options.map(o => (
                            <option key={String(o.value)} value={String(o.value)}>{o.label}</option>
                          ))}
                        </select>
                      ) : (
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={val === true}
                            onChange={e => setFeature(fk, e.target.checked)}
                            className="w-4 h-4 accent-blue-600"
                          />
                          {val === true
                            ? <FiCheck size={14} className="text-green-600" />
                            : <FiX    size={14} className="text-red-400" />
                          }
                        </div>
                      )}
                    </Field>
                  );
                })}
              </Section>

            </div>

            {/* Barre de sauvegarde sticky en bas si dirty */}
            {dirty && (
              <div className="sticky bottom-0 px-6 py-3 bg-orange-50 border-t border-orange-200 flex items-center justify-between">
                <span className="text-sm text-orange-700 flex items-center gap-1.5">
                  <FiInfo size={14} /> Modifications non sauvegardées
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setDraft(JSON.parse(JSON.stringify(plans[selected]))); setDirty(false); }}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-white transition"
                  >
                    Annuler
                  </button>
                  <button onClick={save} disabled={saving}
                    className="flex items-center gap-2 px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition">
                    {saving
                      ? <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Sauvegarde…</>
                      : <><FiSave size={13} /> Sauvegarder</>
                    }
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminPlans;
